import { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import theme from '../theme';
import ResetTourButton from '../components/ResetTourButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrokerSettingsPage({ user }) {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [specInput, setSpecInput] = useState('');
  const [notifPrefs, setNotifPrefs] = useState({ email: true, sms: false, push: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/broker/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data;
      setDisplayName(d.display_name || '');
      setEmail(d.email || '');
      setSpecialties(d.specialties || []);
      setNotifPrefs(d.notification_prefs || { email: true, sms: false, push: true });
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/broker/settings`,
        { name: displayName, specialties, notification_prefs: notifPrefs },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Settings saved');
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addSpecialty = () => {
    const val = specInput.trim();
    if (!val || specialties.length >= 10 || specialties.includes(val)) return;
    setSpecialties([...specialties, val.slice(0, 30)]);
    setSpecInput('');
  };

  const removeSpecialty = (idx) => {
    setSpecialties(specialties.filter((_, i) => i !== idx));
  };

  if (loading) {
    return <div style={{ padding: '32px', color: theme.colors.text.muted }}>Loading...</div>;
  }

  return (
    <div data-testid="broker-settings-page">
      <div style={{ marginBottom: '32px' }}>
        <h1
          style={{
            fontFamily: theme.fonts.heading,
            fontSize: '28px',
            fontWeight: '700',
            color: theme.colors.text.primary,
            marginBottom: '8px',
          }}
        >
          Settings
        </h1>
        <p style={{ color: theme.colors.text.secondary, fontSize: '15px' }}>
          Manage your broker profile and notification preferences.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '680px' }}>
        {/* Profile */}
        <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
          <CardHeader>
            <CardTitle style={{ color: theme.colors.primary, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Settings size={18} /> Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label style={{ color: theme.colors.text.primary }}>Display Name</Label>
              <Input
                data-testid="broker-settings-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={80}
                style={{ background: '#F8F9FB', borderColor: '#E6E8EF', color: '#0B0B0B' }}
              />
            </div>
            <div>
              <Label style={{ color: theme.colors.text.muted }}>Email (read-only)</Label>
              <Input
                value={email}
                readOnly
                style={{ background: 'rgba(75, 157, 255, 0.03)', borderColor: 'rgba(75, 157, 255, 0.1)', color: '#999' }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Specialties */}
        <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
          <CardHeader>
            <CardTitle style={{ color: theme.colors.primary }}>Specialties</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
              {specialties.map((s, i) => (
                <span
                  key={i}
                  data-testid={`specialty-tag-${i}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 12px',
                    borderRadius: '999px',
                    fontSize: '13px',
                    background: 'rgba(75, 157, 255, 0.1)',
                    color: '#4B9DFF',
                    fontWeight: '500',
                  }}
                >
                  {s}
                  <button
                    onClick={() => removeSpecialty(i)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
                  >
                    <X size={14} color="#4B9DFF" />
                  </button>
                </span>
              ))}
              {specialties.length === 0 && (
                <p style={{ color: theme.colors.text.muted, fontSize: '13px' }}>No specialties added yet.</p>
              )}
            </div>
            {specialties.length < 10 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Input
                  data-testid="broker-specialty-input"
                  placeholder="e.g. Fashion, Music, Art"
                  value={specInput}
                  onChange={(e) => setSpecInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addSpecialty()}
                  maxLength={30}
                  style={{ background: '#F8F9FB', borderColor: '#E6E8EF', color: '#0B0B0B' }}
                />
                <Button
                  data-testid="add-specialty-btn"
                  onClick={addSpecialty}
                  style={{ background: 'rgba(75, 157, 255, 0.15)', color: '#4B9DFF', whiteSpace: 'nowrap' }}
                >
                  Add
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
          <CardHeader>
            <CardTitle style={{ color: theme.colors.primary }}>Notifications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { key: 'email', label: 'Email Notifications', desc: 'Receive updates via email' },
              { key: 'sms', label: 'SMS Notifications', desc: 'Receive SMS alerts' },
              { key: 'push', label: 'Push Notifications', desc: 'Browser push alerts' },
            ].map((item) => (
              <div
                key={item.key}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                  borderRadius: theme.radius.md,
                  background: 'rgba(75, 157, 255, 0.03)',
                }}
              >
                <div>
                  <p style={{ fontWeight: '500', color: theme.colors.text.primary, fontSize: '14px' }}>{item.label}</p>
                  <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>{item.desc}</p>
                </div>
                <Switch
                  data-testid={`notif-${item.key}`}
                  checked={notifPrefs[item.key]}
                  onCheckedChange={(val) => setNotifPrefs({ ...notifPrefs, [item.key]: val })}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Save */}
        <Button
          data-testid="broker-save-settings-btn"
          onClick={handleSave}
          disabled={saving}
          style={{
            background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent.teal})`,
            color: '#FFF',
            padding: '12px 32px',
            fontSize: '15px',
            fontWeight: '600',
            alignSelf: 'flex-start',
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
        <ResetTourButton user={user} />
      </div>
    </div>
  );
}
